import { useMemo, useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const PAGE_SIZE = 20;

/**
 * usePagedList — small client-side pagination + status filter helper.
 *
 * @param {Array} items                  Raw array.
 * @param {string} statusFilter          Current status filter value ('' = all).
 * @param {(item:any)=>string|undefined} getStatus  Extractor for the item's status.
 * @returns { paged, totalPages, page, setPage, filtered }
 */
export function usePagedList(items, statusFilter, getStatus) {
  const [page, setPage] = useState(1);
  const filtered = useMemo(() => {
    if (!Array.isArray(items)) return [];
    if (!statusFilter) return items;
    return items.filter(i => getStatus(i) === statusFilter);
  }, [items, statusFilter, getStatus]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  // reset page when filter changes / list shrinks
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);
  const paged = useMemo(
    () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filtered, page],
  );
  return { paged, totalPages, page, setPage, filtered, pageSize: PAGE_SIZE };
}

export function AdminPagination({ page, totalPages, setPage, totalItems = 0, pageSize = PAGE_SIZE, testIdPrefix = 'pagination' }) {
  if (totalItems <= pageSize) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(totalItems, page * pageSize);
  return (
    <div className="flex items-center justify-between gap-3 mt-4 px-1" data-testid={`${testIdPrefix}-bar`}>
      <p className="text-[11px] text-slate-500" data-testid={`${testIdPrefix}-count`}>
        {from}–{to} / {totalItems}
      </p>
      <div className="flex items-center gap-1">
        <Button
          size="sm" variant="outline" className="h-8 w-8 p-0"
          disabled={page <= 1}
          onClick={() => setPage(p => Math.max(1, p - 1))}
          data-testid={`${testIdPrefix}-prev`}
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <span className="px-2 text-xs text-slate-600" data-testid={`${testIdPrefix}-current`}>
          {page} / {totalPages}
        </span>
        <Button
          size="sm" variant="outline" className="h-8 w-8 p-0"
          disabled={page >= totalPages}
          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
          data-testid={`${testIdPrefix}-next`}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
